import {
  AfterViewInit,
  Directive,
  ElementRef,
  NgZone,
  OnDestroy,
  PLATFORM_ID,
  effect,
  inject,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
  DfpModuleColumn,
  DfpModuleKey,
  DfpModulesState,
  planDfpMasonry,
  stackDfpMasonry,
} from '@features/dashboard/performance/pages/dfp/services/dfp-modules.state';

/** Abaixo desta largura de tela as duas colunas não cabem e a grade empilha (a mesma quebra do resto da DFP). */
const STACK_QUERY = '(max-width: 1100px)';
/** Classe que liga o posicionamento absoluto; sem ela (SSR, tela estreita) a grade é a pilha em flex do SCSS. */
const MASONRY_CLASS = 'statement-modules--masonry';
/** Prefixo do id de cada módulo (`statement-module-income`...). */
const MODULE_ID_PREFIX = 'statement-module-';
/** Altura a partir da qual o card já tem a tabela (abaixo disso ainda está carregando ou é só a mensagem de vazio). */
const LOADED_MIN_HEIGHT = 160;

/**
 * Grade em alvenaria da aba Demonstrativos (card 3801, Victor em 30/10/2026). Mede a altura de cada card ligado,
 * pede o plano a `planDfpMasonry` (coluna e topo) e aplica com posição absoluta: assim o Faturamento ligado depois
 * entra embaixo da DRE quando ela termina bem antes do Balanço, em vez de deixar o buraco.
 *
 * Os cards seguem no mesmo lugar do DOM (a exportação Excel lê as quatro tabelas); só o estilo muda. Um
 * `ResizeObserver` refaz o plano quando um card muda de altura (grupo aberto, ano somado, leitura do Cog chegando) ou a
 * grade muda de largura. A largura das colunas vem da divisão salva (`--statement-split`). Em tela estreita e no
 * servidor a classe não entra e a grade fica empilhada.
 */
@Directive({
  selector: '[appStatementMasonry]',
  standalone: true,
})
export class StatementMasonryDirective implements AfterViewInit, OnDestroy {
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly modules = inject(DfpModulesState);
  private readonly zone = inject(NgZone);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private observer?: ResizeObserver;
  private mediaQuery?: MediaQueryList;
  private frame = 0;
  /** Módulos + divisão do último plano completo; igual = só reempilhar com `lockedColumns`. */
  private layoutKey = '';
  private lockedColumns: Partial<Record<DfpModuleKey, DfpModuleColumn>> | null = null;
  private readonly onMediaChange = () => this.schedule();

  constructor() {
    // Ligar, desligar ou reordenar módulos e mudar a divisão pedem um plano novo.
    effect(() => {
      this.modules.modules();
      this.modules.stacked();
      this.host.nativeElement.style.setProperty(
        '--statement-split',
        `${this.modules.splitPercent()}%`
      );
      this.schedule();
    });
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser || typeof ResizeObserver === 'undefined') return;
    this.zone.runOutsideAngular(() => {
      this.observer = new ResizeObserver(() => this.schedule());
      this.observer.observe(this.host.nativeElement);
      this.moduleElements().forEach(element => this.observer!.observe(element));
      this.mediaQuery = window.matchMedia(STACK_QUERY);
      this.mediaQuery.addEventListener('change', this.onMediaChange);
    });
    this.schedule();
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.mediaQuery?.removeEventListener('change', this.onMediaChange);
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  /** Junta as mudanças do mesmo quadro num plano só. */
  private schedule(): void {
    if (!this.isBrowser) return;
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      this.layout();
    });
  }

  private moduleElements(): HTMLElement[] {
    return Array.from(
      this.host.nativeElement.querySelectorAll<HTMLElement>(':scope > .statement-module')
    );
  }

  /**
   * Mede, planeja e aplica. Empilhado (tela estreita ou aba Balanço, DRE ou Faturamento, card 3824) só limpa o que o plano
   * escreveu: cada card em largura total, na ordem do `order` do flex.
   */
  private layout(): void {
    const grid = this.host.nativeElement;
    const elements = this.moduleElements();
    const stacked = !this.mediaQuery || this.mediaQuery.matches || this.modules.stacked();

    if (stacked) {
      grid.classList.remove(MASONRY_CLASS);
      grid.style.height = '';
      elements.forEach(element => this.clear(element));
      this.publish({});
      return;
    }

    grid.classList.add(MASONRY_CLASS);
    const byKey = new Map<DfpModuleKey, HTMLElement>();
    elements.forEach(element => {
      byKey.set(element.id.slice(MODULE_ID_PREFIX.length) as DfpModuleKey, element);
    });
    const order = this.modules.modules().filter(key => byKey.get(key) && !byKey.get(key)!.hidden);
    const heights: Partial<Record<DfpModuleKey, number>> = {};
    order.forEach(key => (heights[key] = byKey.get(key)!.offsetHeight));

    // Coluna só se decide de novo quando os módulos ou a divisão mudam; mudança de altura só reempilha (achado A9 da
    // crítica: o Faturamento não pode pular de coluna porque a leitura do Cog chegou e o painel cresceu).
    // Só trava com todos os cards já com conteúdo: o plano feito com tabelas ainda carregando não vale.
    const loaded = order.every(key => (heights[key] ?? 0) >= LOADED_MIN_HEIGHT);
    const layoutKey = `${order.join(',')}|${this.modules.splitPercent()}|${grid.clientWidth}`;
    const plan =
      loaded && layoutKey === this.layoutKey && this.lockedColumns
        ? stackDfpMasonry(order, heights, this.lockedColumns)
        : planDfpMasonry(order, heights);
    this.layoutKey = loaded ? layoutKey : '';
    const columns: Partial<Record<DfpModuleKey, DfpModuleColumn>> = {};
    byKey.forEach((element, key) => {
      const placement = plan.placements[key];
      if (!placement) {
        this.clear(element);
        return;
      }
      columns[key] = placement.column;
      element.dataset['column'] = placement.column;
      element.style.top = `${placement.top}px`;
    });
    grid.style.height = `${plan.height}px`;
    this.lockedColumns = columns;
    this.publish(columns);
  }

  private clear(element: HTMLElement): void {
    delete element.dataset['column'];
    element.style.top = '';
  }

  /** A coluna decide onde fica a beirada arrastável; o signal é lido no template, então volta para a zona. */
  private publish(columns: Partial<Record<DfpModuleKey, DfpModuleColumn>>): void {
    this.zone.run(() => this.modules.setColumns(columns));
  }
}
